import { forwardRef, memo, useImperativeHandle, useRef, useState } from "react";
import { X } from "lucide-react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import type { DatabookSheetPreview } from "@/lib/api/databook";

export interface CellTextPopoverHandle {
  toggle: (target: HTMLDivElement, address: string, text: string) => void;
  close: () => void;
}

// Keep disclosure state outside the grid: opening it must not render every cell.
export const CellTextPopover = memo(forwardRef<CellTextPopoverHandle, { sheet: DatabookSheetPreview }>(
  function CellTextPopover({ sheet }, ref) {
    const anchor = useRef<HTMLDivElement | null>(null);
    const [expanded, setExpanded] = useState<{
      sheet: DatabookSheetPreview;
      target: HTMLDivElement;
      address: string;
      text: string;
    } | null>(null);
    const active = expanded?.sheet === sheet ? expanded : null;

    useImperativeHandle(ref, () => ({
      close: () => setExpanded(null),
      toggle: (target, address, text) => {
        const content = target.querySelector<HTMLElement>("[data-cell-text]");
        if (!content || !text) {
          setExpanded(null);
          return;
        }
        const textRect = content.getBoundingClientRect();
        const cellRect = target.getBoundingClientRect();
        const clipped = content.scrollWidth > content.clientWidth + 1
          || content.scrollHeight > content.clientHeight + 1
          || textRect.top < cellRect.top - 1
          || textRect.bottom > cellRect.bottom + 1;
        anchor.current = target;
        setExpanded(current => clipped && (current?.sheet !== sheet || current.target !== target)
          ? { sheet, target, address, text }
          : null);
      },
    }), [sheet]);

    return (
      <Popover open={active !== null} onOpenChange={(open) => {
        if (!open) setExpanded(null);
      }}>
        <PopoverAnchor virtualRef={anchor} />
        {active && (
          <PopoverContent
            align="start"
            side="bottom"
            aria-label={`Full text of ${active.address}`}
            className="w-[min(30rem,calc(100vw-2rem))] !animate-none p-3"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            onInteractOutside={(event) => {
              if (event.target instanceof Node && active.target.contains(event.target)) event.preventDefault();
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onCopy={(event) => event.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="font-mono text-xs text-muted-foreground">{active.address}</span>
              <button type="button" aria-label="Close cell text" className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => setExpanded(null)}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="max-h-64 select-text overflow-y-auto whitespace-pre-wrap break-words text-sm leading-relaxed">
              {active.text}
            </div>
          </PopoverContent>
        )}
      </Popover>
    );
  },
));
